import { Box, Stack, Typography } from "@mui/material";
import { HubCard } from "~/components/hub-card";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { Flex } from "~/components/Flex";
import { useSprintStreakCard } from "./hooks/useSprintStreakCard";
import { SprintStreakCardSkeleton } from "~/pages/space/home-dashboard/SprintStreakCard/components/SprintStreakCardSkeleton";
import { SprintStreakCardEmptyState } from "~/pages/space/home-dashboard/SprintStreakCard/components/SprintStreakCardEmptyState";
import { SprintStreakCardChart } from "~/pages/space/home-dashboard/SprintStreakCard/components/SprintStreakCardChart";
import { SprintStreakCardDelta } from "~/pages/space/home-dashboard/SprintStreakCard/components/SprintStreakCardDelta";
import { FormatDateYear } from "~/lib/date";

export interface SprintStreakCardProps {
  scope: SpaceOrWorkspaceScope;
}

export function SprintStreakCard({ scope }: SprintStreakCardProps) {
  const { isLoading, isEmpty, dailySpaceRisk } = useSprintStreakCard({
    scope,
  });

  const firstDay = dailySpaceRisk.at(0);
  const lastDay = dailySpaceRisk.at(-1);

  const startRisk = firstDay?.startRisk ?? 0;
  const endRisk = lastDay?.endRisk ?? 0;
  const riskDelta = endRisk - startRisk;

  const timeRangeSubtitle = [firstDay?.time, lastDay?.time]
    .flatMap((t) => FormatDateYear(t))
    .join(" - ");

  const tagline = (
    <Flex
      sx={{
        color: "text.secondary",
        alignItems: "center",
        gap: 0.5,
        mt: 0.8,
        textTransform: "initial",
      }}
    >
      <Typography
        color="text.secondary"
        textTransform="uppercase"
        fontSize={12}
      >
        {timeRangeSubtitle}
      </Typography>
    </Flex>
  );

  const title = (
    <Flex alignItems="center" gap={1}>
      <Typography
        variant="h5"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontWeight: 700,
          mb: 0,
          fontSize: { xs: 20, md: 24 },
        }}
      >
        Space Risk Trend
      </Typography>
      <SprintStreakCardDelta riskDelta={riskDelta} type="axis" />
    </Flex>
  );

  return (
    <HubCard
      loading={false}
      title={title}
      tagline={tagline}
      data-testid="sprint-streak-card"
      sx={{
        width: "100%",
      }}
    >
      <Flex justifyContent="space-between" flexDirection="column" height="100%">
        {isLoading && <SprintStreakCardSkeleton />}

        {!isLoading && isEmpty && <SprintStreakCardEmptyState />}

        {!isLoading && !isEmpty && (
          <Stack
            direction="row"
            gap={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ flexGrow: 1 }}>
              <SprintStreakCardChart data={dailySpaceRisk} />
            </Box>
          </Stack>
        )}
      </Flex>
    </HubCard>
  );
}
